import { Feature, FeatureCollection } from 'geojson';
import { MAPBOX_ACCESS_TOKEN } from './map';

const STATIC_BASE =
    'https://api.mapbox.com/styles/v1/mapbox/streets-v11/static';

export interface StaticMapOptions {
    width?: number; // px before retina scaling
    height?: number;
    retina?: boolean; // append @2x
    padding?: number; // px around the auto-fit bounds
    strokeColor?: string;
    fillColor?: string;
    fillOpacity?: number;
}

const DEFAULTS: Required<StaticMapOptions> = {
    width: 120,
    height: 80,
    retina: true,
    padding: 20,
    strokeColor: '#3bb2d0',
    fillColor: '#3bb2d0',
    fillOpacity: 0.3
};

// simplestyle-spec props so the Static API renders a filled, outlined overlay
const styleProps = (o: Required<StaticMapOptions>) => ({
    stroke: o.strokeColor,
    'stroke-width': 2,
    fill: o.fillColor,
    'fill-opacity': o.fillOpacity
});

const applyStyle = (
    geojson: Feature | FeatureCollection,
    o: Required<StaticMapOptions>
): Feature | FeatureCollection => {
    const style = styleProps(o);
    if (geojson.type === 'FeatureCollection') {
        return {
            ...geojson,
            features: geojson.features.map((f) => ({
                ...f,
                properties: { ...f.properties, ...style }
            }))
        };
    }
    return { ...geojson, properties: { ...geojson.properties, ...style } };
};

/**
 * Build a Mapbox Static Images API URL that renders the given GeoJSON overlay,
 * auto-fitted to its bounds. Returns a plain image URL for use in an <img>.
 * Domain-agnostic and reusable across features.
 */
export const buildStaticMapImageUrl = (
    geojson: Feature | FeatureCollection,
    options: StaticMapOptions = {}
): string => {
    const o = { ...DEFAULTS, ...options };
    const styled = applyStyle(geojson, o);
    const overlay = `geojson(${encodeURIComponent(JSON.stringify(styled))})`;
    const size = `${o.width}x${o.height}${o.retina ? '@2x' : ''}`;
    return `${STATIC_BASE}/${overlay}/auto/${size}?padding=${o.padding}&access_token=${MAPBOX_ACCESS_TOKEN}`;
};
